<script lang="ts">
  import { tick } from "svelte";
  import { statusLabels } from "@/config";
  import {
    consoleConnection,
    consoleError,
    consoleAgentOnline,
    consoleLines,
    consoleStatus,
    consoleServerId,
    send,
  } from "@/stores/console";
  import { detectCrashFromLogs, type CrashDiagnostic } from "@/utils/crash-detector";
  import { commandHistory, rememberCommand } from "@/stores/console-command-history";
  import { user } from "@/stores/session";

  let input = $state("");
  let sending = $state(false);
  let output = $state<HTMLPreElement>();
  let initialScrolled = false;
  let userPinnedToBottom = $state(true);
  let dismissedCrashId = $state<string | null>(null);
  let historyIndex: number | null = null;
  let draft = "";
  let historyIdentity = "";

  $effect(() => {
    const identity = `${$user?.id ?? ""}:${$consoleServerId ?? ""}`;
    if (historyIdentity !== identity) {
      historyIdentity = identity;
      historyIndex = null;
      draft = "";
      input = "";
    }
  });

  const activeCrash = $derived<CrashDiagnostic | null>(
    detectCrashFromLogs($consoleLines, $consoleServerId ?? undefined),
  );
  const crashVisible = $derived(activeCrash !== null && dismissedCrashId !== activeCrash.id);

  const connected = $derived($consoleConnection === "connected");
  const consoleReady = $derived(
    connected && $consoleAgentOnline === true && $consoleStatus === "running",
  );
  const canSend = $derived(consoleReady && input.trim().length > 0 && !sending);

  const lineBreak = "\n";

  type AnsiSegment = { text: string; className: string };

  function renderAnsi(line: string): AnsiSegment[] {
    const segments: AnsiSegment[] = [];
    let className = "";
    let cursor = 0;
    // Keep SGR color commands, discard terminal cursor/erase commands such
    // as ESC[K that are useful in a TTY but are noise in a log history.
    // ANSI escape is intentionally matched to interpret terminal colors.
    // eslint-disable-next-line no-control-regex
    const pattern = /\u001b\[([0-9;?]*)([ -/]*)([@-~])/g;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line))) {
      if (match.index > cursor) {
        segments.push({ text: line.slice(cursor, match.index), className });
      }
      if (match[3] === "m") {
        const codes = (match[1] || "0").split(";").map(Number);
        for (const code of codes) {
          if (code === 0) {
            className = "";
          } else if (code === 1) {
            className = `${className} ansi-bold`;
          } else if (code === 2) {
            className = `${className} ansi-dim`;
          } else if (code >= 30 && code <= 37) {
            className = `${className} ansi-color-${code - 30}`;
          } else if (code >= 90 && code <= 97) {
            className = `${className} ansi-color-bright-${code - 90}`;
          }
        }
      }
      cursor = pattern.lastIndex;
    }
    if (cursor < line.length) {
      segments.push({ text: line.slice(cursor), className });
    }
    return segments.length ? segments : [{ text: line, className: "" }];
  }

  function scrollToBottom(behavior: ScrollBehavior = "instant"): void {
    if (!output) {
      return;
    }
    output.scrollTo({ top: output.scrollHeight, behavior });
    userPinnedToBottom = true;
  }

  function handleScroll(): void {
    if (!output) {
      return;
    }
    const distance = output.scrollHeight - output.scrollTop - output.clientHeight;
    userPinnedToBottom = distance <= 80;
  }

  // При получении новых строк или первого батча логов скроллим вниз
  $effect(() => {
    const lines = $consoleLines;
    const lineCount = lines.length;
    if (lineCount === 0) {
      return;
    }

    const shouldScroll = !initialScrolled || userPinnedToBottom;

    void tick().then(() => {
      if (!output) {
        return;
      }
      if (shouldScroll) {
        output.scrollTop = output.scrollHeight;
        initialScrolled = true;
        requestAnimationFrame(() => {
          if (output && shouldScroll && userPinnedToBottom) {
            output.scrollTop = output.scrollHeight;
          }
        });
      }
    });
  });

  // При монтировании элемента <pre> скроллим к уже загруженным строкам
  $effect(() => {
    if (output && $consoleLines.length > 0 && !initialScrolled) {
      void tick().then(() => {
        if (output) {
          output.scrollTop = output.scrollHeight;
          initialScrolled = true;
          setTimeout(() => {
            if (output && userPinnedToBottom) {
              output.scrollTop = output.scrollHeight;
            }
          }, 60);
        }
      });
    }
  });

  async function submit(): Promise<void> {
    if (!canSend) {
      return;
    }
    sending = true;
    const command = input.trim();
    const serverId = $consoleServerId;
    const accountId = $user?.id;
    try {
      await send(command);
      if ($user?.id === accountId) {
        rememberCommand(serverId, command);
      }
      if ($consoleServerId === serverId && $user?.id === accountId) {
        input = "";
        historyIndex = null;
        draft = "";
      }
      userPinnedToBottom = true;
      await tick();
      scrollToBottom();
    } catch {
      // The transport exposes the server error next to the console.
    } finally {
      sending = false;
    }
  }

  function handleKeydown(event: KeyboardEvent): void {
    if (event.isComposing) {
      return;
    }
    if (
      (event.key === "ArrowUp" || event.key === "ArrowDown") &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !event.metaKey
    ) {
      const textarea = event.currentTarget as HTMLTextAreaElement;
      const caret = textarea.selectionStart;
      const atBoundary =
        textarea.selectionEnd === caret &&
        (event.key === "ArrowUp"
          ? !input.slice(0, caret).includes("\n")
          : !input.slice(caret).includes("\n"));
      const history = commandHistory($consoleServerId);
      if (
        !atBoundary ||
        history.length === 0 ||
        (event.key === "ArrowDown" && historyIndex === null)
      ) {
        return;
      }
      event.preventDefault();
      if (historyIndex === null) {
        draft = input;
        historyIndex = history.length - 1;
      } else if (event.key === "ArrowUp") {
        historyIndex = Math.max(0, historyIndex - 1);
      } else if (historyIndex < history.length - 1) {
        historyIndex += 1;
      } else {
        historyIndex = null;
      }
      input = historyIndex === null ? draft : history[historyIndex];
      void tick().then(() => textarea.setSelectionRange(input.length, input.length));
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      void submit();
    }
  }
</script>

<section class="panel-card console-box live-console" aria-labelledby="console-title">
  <header class="console-head">
    <div>
      <span id="console-title">Консоль</span>
      <small class:online={connected && $consoleAgentOnline === true}>
        {$consoleConnection !== "connected"
          ? $consoleConnection === "reconnecting"
            ? "переподключение"
            : $consoleConnection === "connecting"
              ? "соединение с панелью"
              : "панель недоступна"
          : $consoleAgentOnline
            ? "связь с сервером установлена"
            : "оборудование хостинга недоступно"}
      </small>
    </div>
    {#if $consoleStatus}
      <span class="console-status">{statusLabels[$consoleStatus] ?? $consoleStatus}</span>
    {/if}
  </header>
  {#if $consoleError}
    <div class="console-error" role="alert">{$consoleError}</div>
  {/if}

  <div class="console-output-wrap">
    <pre
      bind:this={output}
      onscroll={handleScroll}>{#if $consoleLines.length}{#each $consoleLines as line, index (index)}{#each renderAnsi(line) as segment, segmentIndex (segmentIndex)}<span
              class={segment.className}>{segment.text}</span
            >{/each}{#if index < $consoleLines.length - 1}{lineBreak}{/if}{/each}{:else}Здесь появятся сообщения сервера после начала установки или запуска.{/if}</pre>
    {#if !userPinnedToBottom && $consoleLines.length > 0}
      <button type="button" class="scroll-bottom-btn" onclick={() => scrollToBottom("smooth")}>
        Вниз ↓
      </button>
    {/if}
  </div>

  {#if crashVisible && activeCrash}
    <div class="crash-copilot-banner" role="alert">
      <div class="crash-icon" aria-hidden="true">⚡</div>
      <div class="crash-content">
        <div class="crash-head">
          <span class="crash-badge">Crash Copilot</span>
          <strong class="crash-title">{activeCrash.title}</strong>
        </div>
        <p class="crash-reason">{activeCrash.reason}</p>
        <p class="crash-advice"><strong>Решение:</strong> {activeCrash.advice}</p>
      </div>
      <div class="crash-actions">
        {#if activeCrash.actionHref && activeCrash.actionLabel}
          <a class="button button-action" href={activeCrash.actionHref}>
            {activeCrash.actionLabel}
          </a>
        {/if}
        <button
          type="button"
          class="dismiss-button"
          onclick={() => (dismissedCrashId = activeCrash.id)}
          aria-label="Скрыть подсказку"
          title="Скрыть подсказку"
        >
          ✕
        </button>
      </div>
    </div>
  {/if}

  <form
    class="console-input"
    onsubmit={(event) => {
      event.preventDefault();
      void submit();
    }}
  >
    <label for="console-command">Команда сервера</label>
    {#if !consoleReady}<p class="console-hint">
        Команды станут доступны, когда сервер запустится и связь с ним восстановится.
      </p>{/if}
    <div>
      <textarea
        id="console-command"
        bind:value={input}
        maxlength="500"
        onkeydown={handleKeydown}
        rows="1"
        disabled={!consoleReady || sending}
        placeholder={consoleReady ? "Например: say Привет" : "Команды пока недоступны"}></textarea>
      <button class="button" type="submit" disabled={!canSend}
        >{sending ? "Отправляем…" : "Отправить"}</button
      >
    </div>
    <small>Enter отправляет · Shift+Enter переносит строку · ↑↓ история команд</small>
  </form>
</section>

<style>
  .live-console {
    overflow: hidden;
    border: 1px solid var(--line);
    background: var(--ink-deep);
  }
  .console-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.8rem 0.95rem;
    border-bottom: 1px solid var(--line);
    color: var(--dim);
    font-size: 0.67rem;
    text-transform: uppercase;
  }
  .console-head > div {
    display: flex;
    align-items: center;
    gap: 0.7rem;
  }
  .console-head small {
    color: var(--mars-light);
    font-size: 0.57rem;
  }
  .console-head small.online {
    color: var(--green);
  }
  .console-status {
    color: var(--cream);
  }
  .console-error {
    padding: 0.65rem 0.9rem;
    border-bottom: 1px solid rgb(214 75 50 / 0.3);
    color: var(--mars-light);
    background: rgb(214 75 50 / 0.08);
    font-size: 0.72rem;
  }
  .console-output-wrap {
    position: relative;
    width: 100%;
  }
  pre {
    min-height: 16rem;
    max-height: 28rem;
    overflow: auto;
    margin: 0;
    padding: 1rem;
    color: #a6c49a;
    font:
      400 0.72rem/1.6 ui-monospace,
      SFMono-Regular,
      Menlo,
      Consolas,
      monospace;
    white-space: pre-wrap;
    word-break: break-word;
  }
  .scroll-bottom-btn {
    position: absolute;
    bottom: 1rem;
    right: 1.25rem;
    background: var(--surface, #1e1e24);
    color: var(--cream, #fff);
    border: 1px solid var(--line-strong, #444);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    padding: 0.35rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      opacity 0.2s,
      transform 0.15s,
      background 0.15s;
    z-index: 10;
  }
  .scroll-bottom-btn:hover {
    background: var(--bg-hover, #2e2e38);
    transform: translateY(-1px);
  }
  .ansi-bold {
    font-weight: 700;
  }
  .ansi-dim {
    opacity: 0.72;
  }
  .ansi-color-0 {
    color: #000;
  }
  .ansi-color-1 {
    color: #c94b4b;
  }
  .ansi-color-2 {
    color: #72b66b;
  }
  .ansi-color-3 {
    color: #d8b45b;
  }
  .ansi-color-4 {
    color: #668dcc;
  }
  .ansi-color-5 {
    color: #b477c4;
  }
  .ansi-color-6 {
    color: #61bdbd;
  }
  .ansi-color-7 {
    color: #d8d3c9;
  }
  .ansi-color-bright-0 {
    color: #666;
  }
  .ansi-color-bright-1 {
    color: #ff7777;
  }
  .ansi-color-bright-2 {
    color: #9bdc8f;
  }
  .ansi-color-bright-3 {
    color: #f1d27c;
  }
  .ansi-color-bright-4 {
    color: #8fb3ff;
  }
  .ansi-color-bright-5 {
    color: #df9bea;
  }
  .ansi-color-bright-6 {
    color: #8be5e5;
  }
  .ansi-color-bright-7 {
    color: #fff9ec;
  }
  .crash-copilot-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding: 0.75rem 1rem;
    border-top: 1px solid rgba(240, 112, 79, 0.35);
    border-bottom: 1px solid rgba(240, 112, 79, 0.2);
    background: linear-gradient(90deg, rgba(214, 75, 50, 0.12) 0%, rgba(24, 24, 28, 0.85) 100%);
    font-size: 0.75rem;
    line-height: 1.45;
  }
  .crash-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 4px;
    background: rgba(240, 112, 79, 0.2);
    color: var(--mars-light);
    font-size: 0.9rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
  .crash-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .crash-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
  .crash-badge {
    display: inline-block;
    padding: 0.1rem 0.4rem;
    border-radius: 3px;
    background: var(--action);
    color: #fff;
    font-size: 0.58rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .crash-title {
    color: var(--cream);
    font-weight: 600;
  }
  .crash-reason {
    margin: 0;
    color: var(--dim);
    font-size: 0.72rem;
  }
  .crash-advice {
    margin: 0;
    color: #f1d27c;
    font-size: 0.73rem;
  }
  .crash-advice strong {
    color: #fff;
  }
  .crash-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }
  .button-action {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.7rem;
    font-size: 0.68rem;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    border-radius: var(--radius-sm, 4px);
    background: var(--surface);
    color: var(--mars-light);
    border: 1px solid var(--mars-light);
    transition:
      background 0.15s,
      color 0.15s;
  }
  .button-action:hover {
    background: var(--mars-light);
    color: #fff;
  }
  .dismiss-button {
    background: transparent;
    border: none;
    color: var(--dim);
    font-size: 0.9rem;
    cursor: pointer;
    padding: 0.25rem 0.45rem;
    border-radius: 4px;
    line-height: 1;
    transition:
      color 0.15s,
      background 0.15s;
  }
  .dismiss-button:hover {
    color: var(--cream);
    background: rgba(255, 255, 255, 0.08);
  }
  @media (max-width: 640px) {
    .console-head {
      flex-wrap: wrap;
    }
    .console-head > div {
      flex-wrap: wrap;
    }
    .crash-copilot-banner {
      flex-direction: column;
    }
    .crash-actions {
      width: 100%;
      justify-content: space-between;
      margin-top: 0.4rem;
    }
  }
  .console-input {
    padding: 0.85rem;
    border-top: 1px solid var(--line);
  }
  .console-input label,
  .console-input small {
    color: var(--dim);
    font-size: 0.62rem;
  }
  .console-hint {
    margin: 0 0 0.75rem;
    color: var(--muted);
    font-size: 0.8rem;
    line-height: 1.5;
  }
  .console-input label {
    display: block;
    margin-bottom: 0.45rem;
    text-transform: uppercase;
  }
  .console-input > div {
    display: flex;
    gap: 0.65rem;
  }
  textarea {
    flex: 1;
    min-width: 0;
    resize: vertical;
    padding: 0.75rem;
    border: 1px solid var(--line-strong);
    color: var(--cream);
    background: var(--surface);
    font:
      400 0.78rem/1.4 ui-monospace,
      monospace;
  }
  textarea:focus {
    outline: 1px solid var(--mars-light);
  }
  .console-input .button {
    min-height: 2.7rem;
    padding-inline: 0.9rem;
  }
  .console-input small {
    display: block;
    margin-top: 0.45rem;
  }
  @media (max-width: 520px) {
    .console-input > div {
      align-items: stretch;
      flex-direction: column;
    }
    .console-input .button {
      width: 100%;
    }
  }
</style>
